import AutocompleteSelect from '@/gf/components/AutocompleteSelect'
import Button from '@/gf/components/ButtonOld'
import Card from '@/gf/components/Card'
import ModalForm from '@/gf/components/ModalForm'
import PriceField from '@/gf/components/PriceField'
import TextField from '@/gf/components/TextField'
import useMsgs from '@/gf/hooks/useMsgs'
import useToggle from '@/gf/hooks/useToggle'
import UserContext from '@/gf/modules/User'
import * as api from '@/suppliers/modules/api'
import React from 'react'
import { EditLineItem, ProductSearchResult, StoreLookup, StoreOption } from '../../types'
import useSession from '../hooks/useSession'
import AddProductFilters from './AddProductFilters'

const initial = { value: '', label: '' }

const AddItemsCard = ({
  allStores,
  productsFromQuery,
  setProductsFromQuery,
  newOrderLineItems,
  addProduct,
}: {
  allStores: StoreLookup[]
  productsFromQuery: ProductSearchResult[]
  setProductsFromQuery: React.Dispatch<React.SetStateAction<ProductSearchResult[]>>
  newOrderLineItems: EditLineItem[]
  addProduct: (product: ProductSearchResult) => void
}) => {
  const [selectedStore, setSelectedStore] = React.useState<StoreOption>(initial)
  const [customProductName, setCustomProductName] = React.useState('')
  const [customProductPrice, setCustomProductPrice] = React.useState('')
  const [customProductMPN, setCustomProductMPN] = React.useState('')

  const { user, store } = useSession()
  const [_msgs, msgsMgr] = useMsgs()

  const [customProductModalOpen, customProductModalToggler] = useToggle()

  const adminCreateCustomProduct = async () =>
    selectedStore.value === ''
      ? api.createOfflineProduct(
          customProductName,
          parseFloat(customProductPrice).toFixed(2),
          customProductMPN
        )
      : api.createOfflineProduct(
          customProductName,
          parseFloat(customProductPrice).toFixed(2),
          customProductMPN,
          selectedStore.value
        )

  const supplierCreateCustomProduct = async () =>
    api.createOfflineProduct(
      customProductName,
      parseFloat(customProductPrice).toFixed(2),
      customProductMPN,
      store.id
    )

  const createAndAddCustomProduct = async () => {
    const response = UserContext.isAdmin(user)
      ? await adminCreateCustomProduct()
      : await supplierCreateCustomProduct()

    const { status } = response
    if (status !== 200) {
      const body = await response.text()
      msgsMgr.add(body, 'negative')
    } else {
      const productId = await response.text()
      // need to convert product -> productSearchResult type by adding inOrder boolean
      api.fetchProduct(productId).then((product) => addProduct({ ...product, inOrder: false }))

      setSelectedStore(initial)
      setCustomProductName('')
      setCustomProductPrice('')
      setCustomProductMPN('')
    }
    customProductModalToggler.off()
  }

  const storeOptions = allStores.map((sto) => ({
    value: sto.id,
    label: `${sto.name}${sto?.alias ? ` (${sto.alias})` : ''}`,
  }))

  return (
    <div>
      <ModalForm
        open={customProductModalOpen}
        onClose={customProductModalToggler.off}
        title="Add Custom Product"
        onSubmit={(e) => {
          e.preventDefault()
          createAndAddCustomProduct()
        }}
        submitButtonText="Add"
      >
        <p className="text-sm text-gray-400 mb-2">
          Custom products are not public on gearflow.com.
        </p>
        {user && UserContext.isAdmin(user) && (
          <AutocompleteSelect
            label="Store"
            name="storeSelection"
            className="w-full text-sm mb-1"
            value={allStores.find((s) => s.id === selectedStore.value)}
            options={storeOptions}
            onChange={setSelectedStore}
            reactSelectOptions={{ isSearchable: true, isMulti: false }}
          />
        )}
        <TextField
          label="Name"
          required
          onChange={(e) => setCustomProductName(e.target.value)}
          value={customProductName}
        />
        <PriceField
          label="Price"
          required
          onChange={(e) => setCustomProductPrice(e.target.value)}
          type="text"
          value={customProductPrice}
          min={0.01}
        />
        <TextField
          label="MPN"
          required
          onChange={(e) => setCustomProductMPN(e.target.value)}
          value={customProductMPN}
        />
      </ModalForm>

      <Card
        title="Add Items"
        headerButton={
          <Button primary={false} type="button" onClick={() => customProductModalToggler.on()}>
            Add Custom Item
          </Button>
        }
        className={!store.hasCatalog ? 'pb-6' : undefined}
      >
        {store.hasCatalog && (
          <AddProductFilters
            restrictToStoreId={UserContext.isAdmin(user) ? undefined : store.id}
            addProduct={addProduct}
            newOrderLineItems={newOrderLineItems}
            setProductsFromQuery={setProductsFromQuery}
            productsFromQuery={productsFromQuery}
          />
        )}
      </Card>
    </div>
  )
}

export default AddItemsCard
